import { getEntityRef } from "@client/content/lib/helper";
import { makeModuleApiInfoSelector } from "@client/content/selectors";
import { type RootContainerProps } from "@client/content/types";
import { apiAction } from "@client/lib/actions";
import { TargetSelector, getSettingValueSelector } from "@client/selectors";
import { type AppState, type EntityRef } from "@client/types";
import { type ReduxAction } from "@client/types/ReduxAction";
import { type Dispatch } from "redux";
import { initAction } from "../actions";
import api from "./api";
import { getCodes } from "./lib";
import { makeAlertSelector } from "./selectors";
import { type Purchase } from "./types";

export enum Actions {
  InitBegin = "MODULE_PURCHASE_INIT_BEGIN",
  InitFailure = "MODULE_PURCHASE_INIT_FAILURE",
  InitSuccess = "MODULE_PURCHASE_INIT_SUCCESS",

  SetupBegin = "MODULE_PURCHASE_SETUP_BEGIN",
  SetupFailure = "MODULE_PURCHASE_SETUP_FAILURE",
  SetupSuccess = "MODULE_PURCHASE_SETUP_SUCCESS",

  SystemAssigned = "MODULE_PURCHASE_SYSTEM_ASSIGNED",
  SystemAssignFailed = "MODULE_PURCHASE_SYSTEM_ASSIGN_FAILED",
  UserVerified = "MODULE_PURCHASE_USER_VERIFIED",

  ProceedAlert = "MODULE_PURCHASE_PROCEED_ALERT",
  SetError = "MODULE_PURCHASE_SET_ERROR",
  GoToStep = "MODULE_PURCHASE_GO_TO_STEP"
}

export type InitBeginActionData = EntityRef &
  Purchase.Api.Codes & {
    packageId: string;
    trialId: string | null;
  };
export type InitSuccessActionData = Purchase.Api.InitResponse;
const _initAsync = initAction<AppState, InitBeginActionData, Purchase.Api.InitResponse, InitSuccessActionData>({
  types: {
    begin: Actions.InitBegin,
    failure: Actions.InitFailure,
    success: Actions.InitSuccess
  },
  action: (state: AppState, dispatch: any, args: InitBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, args);
    return api.init(apiInfo, args);
  }
});

export const init = (props: EntityRef) => (dispatch: Dispatch<any>, getState: () => AppState) => {
  const state = getState();

  const packageIdSelector = getSettingValueSelector(TargetSelector.Entity, "package");
  const trialIdSelector = getSettingValueSelector(TargetSelector.Entity, "trial");
  dispatch(
    _initAsync({
      ...props,
      packageId: packageIdSelector(state, props),
      trialId: trialIdSelector(state, props),
      ...getCodes()
    })
  );
};

export type SetupBeginActionData = EntityRef & Purchase.Api.SetupRequest;
export interface SetupSuccessActionData {
  redirectUrl: string;
}
const _setupAsync = apiAction<AppState, SetupBeginActionData, Purchase.Api.SetupResponse, SetupSuccessActionData>({
  types: {
    begin: Actions.SetupBegin,
    failure: Actions.SetupFailure,
    success: Actions.SetupSuccess
  },
  action: (state: AppState, dispatch: any, { id, source, ...args }: SetupBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, { id, source });
    return api.setup(apiInfo, args);
  }
});

export const systemAssigned =
  (props: EntityRef, res: Purchase.Api.AssignSystemResponse) => (dispatch: Dispatch<any>, getState: () => AppState) => {
    dispatch(_setupAsync({ ...props, ...res }));
  };

export type SystemAssignFailedActionData = EntityRef & {
  error: Purchase.Api.ErrorCode;
};
export const systemAssignFailed =
  (props: EntityRef, error: Purchase.Api.ErrorCode) => (dispatch: Dispatch<any>, getState: () => AppState) => {
    dispatch({
      type: Actions.SystemAssignFailed,
      data: <SystemAssignFailedActionData>{
        ...getEntityRef(props),
        error
      }
    });
  };

export type ProceedAlertActionData = EntityRef & {
  proceed: boolean;
};

export const acceptAlert = (props: RootContainerProps) => ({
  type: Actions.ProceedAlert,
  data: <ProceedAlertActionData>{ proceed: true, id: props.id }
});

export const discardAlert = (props: RootContainerProps) => ({
  type: Actions.ProceedAlert,
  data: <ProceedAlertActionData>{ proceed: false, id: props.id }
});

export type SetErrorActionData = EntityRef & {
  error: null;
};
export const setError = (props: RootContainerProps, error: null) => ({
  type: Actions.SetError,
  data: <SetErrorActionData>{ error, id: props.id }
});

export type GoToStepActionData = EntityRef & {
  step: number;
};

export const goToStep = (props: RootContainerProps, step: number): ReduxAction<GoToStepActionData> => ({
  type: Actions.GoToStep,
  data: { ...getEntityRef(props), step }
});

export type UserVerifiedActionData = {
  alert: Purchase.Alert | null;
  data: Purchase.View.FirstStepFormData;
  result: Purchase.Api.VerifyUserResponse;
};
export const userVerified =
  (ref: EntityRef, data: Purchase.View.FirstStepFormData, result: Purchase.Api.VerifyUserResponse) =>
  (dispatch: Dispatch<any>, getState: () => AppState) => {
    const state = getState();

    dispatch({
      type: Actions.UserVerified,
      data: <UserVerifiedActionData>{
        id: ref.id,
        alert: makeAlertSelector()(state, { errorCode: result.errorCode, ...ref }),
        data,
        result
      }
    });
  };
